import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { downloadCompsXlsx } from './compsXlsx'

// Customer Service -> Comps (2026-10-06). Complimentary tickets issued to ID-card
// holders (staff, volunteers, associates), from /api/customer-service/comps
// (cache.CompUsage / cache.CompPeople, Sync Hub Refresh-Comps nightly at 02:50).
//
//   * season picker (this season by default), "Matched users only" (off by default)
//   * summary panel top-right: comps consumed, total value, people
//   * one row per person, click-sortable columns, default sort surname then comps
//   * click a row -> that person's comps by session (train, date & time, qty,
//     value) with a total
//   * Download -> the three-sheet workbook (compsXlsx.ts)

export interface CompPerson {
  id_number: number
  memb_no: number
  forename: string | null
  surname: string | null
  status: string
  matched: boolean
  ontrack_matched: boolean
  card_no: string | null
  card_inactive: boolean | null
  email: string | null
  comps: number
  prev_comps: number
  value: number
  last_used: string | null
  last_session: string | null
}
export interface CompLine {
  id_number: number
  txn_no: number
  txn_seq: number
  sale_at: string
  session_at: string | null
  session_desc: string | null
  qty: number
  unit_value: number
  value: number
  value_basis: string
}
interface CompsPayload {
  season: string
  prev_season: string
  seasons: string[]
  generated_at: string | null
  usage_rows: number
  loaded_from: string | null
  value_basis: string | null
  people: CompPerson[]
  lines: CompLine[]
}

const TH: CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#6b6b6b', fontWeight: 500, fontSize: '0.78rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff', cursor: 'pointer', userSelect: 'none' }
const TD: CSSProperties = { padding: '6px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f0ece1', verticalAlign: 'top' }
const NUM: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 })
const int = new Intl.NumberFormat('en-GB')

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
}
function fmtCard(card: string | null): string {
  if (!card) return '—'
  const d = card.replace(/\D/g, '')
  return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : card
}
export function personName(p: CompPerson): string {
  return [p.surname, p.forename].filter(Boolean).join(', ') || `ID ${p.id_number}`
}

type SortKey = 'name' | 'status' | 'id_number' | 'card_no' | 'comps' | 'prev_comps' | 'last_used' | 'value' | 'matched'
const DEFAULT_DIR: Record<SortKey, 1 | -1> = { name: 1, status: 1, id_number: 1, card_no: 1, comps: -1, prev_comps: -1, last_used: -1, value: -1, matched: -1 }

function cmp(a: CompPerson, b: CompPerson, key: SortKey): number {
  switch (key) {
    case 'name': return (a.surname ?? '').localeCompare(b.surname ?? '', 'en-GB', { sensitivity: 'base' }) || (a.forename ?? '').localeCompare(b.forename ?? '', 'en-GB', { sensitivity: 'base' })
    case 'status': return a.status.localeCompare(b.status)
    case 'id_number': return a.id_number - b.id_number
    case 'card_no': return (a.card_no ?? '').localeCompare(b.card_no ?? '')
    case 'comps': return a.comps - b.comps
    case 'prev_comps': return a.prev_comps - b.prev_comps
    case 'last_used': return (a.last_used ?? '').localeCompare(b.last_used ?? '')
    case 'value': return a.value - b.value
    case 'matched': return Number(a.matched) - Number(b.matched)
  }
}

export function CompsView() {
  const [season, setSeason] = useState<string>('')
  const [data, setData] = useState<CompsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [matchedOnly, setMatchedOnly] = useState(false) // off by default (Chris, 2026-10-06): show every ID-card holder
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 })
  const [open, setOpen] = useState<number | null>(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    let live = true
    setLoading(true)
    const qs = season ? `?season=${encodeURIComponent(season)}` : ''
    fetch(`/api/customer-service/comps${qs}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<CompsPayload> })
      .then((d) => { if (live) { setData(d); setError(null); if (!season) setSeason(d.season) } })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [season])

  const people = useMemo(() => {
    const all = data?.people ?? []
    const filtered = matchedOnly ? all.filter((p) => p.matched) : all
    const sorted = filtered.slice().sort((a, b) => {
      const primary = cmp(a, b, sort.key) * sort.dir
      if (primary !== 0) return primary
      // Secondary: surname, then comps used (most first) - the default order.
      return cmp(a, b, 'name') || (b.comps - a.comps)
    })
    return sorted
  }, [data, matchedOnly, sort])

  const linesByPerson = useMemo(() => {
    const m = new Map<number, CompLine[]>()
    for (const l of data?.lines ?? []) { const a = m.get(l.id_number) ?? []; a.push(l); m.set(l.id_number, a) }
    for (const a of m.values()) a.sort((x, y) => (x.session_at ?? x.sale_at).localeCompare(y.session_at ?? y.sale_at))
    return m
  }, [data])

  const summary = useMemo(() => ({
    comps: people.reduce((a, p) => a + p.comps, 0),
    value: people.reduce((a, p) => a + p.value, 0),
    people: people.length,
    unmatched: (data?.people ?? []).filter((p) => !p.matched).length,
  }), [people, data])

  const onSort = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: DEFAULT_DIR[key] }))
  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : '')

  const onDownload = async () => {
    if (!data) return
    setDownloading(true)
    try {
      const ids = new Set(people.map((p) => p.id_number))
      await downloadCompsXlsx({
        season: data.season, prevSeason: data.prev_season, generatedAt: data.generated_at, valueBasis: data.value_basis,
        matchedOnly, people, lines: data.lines.filter((l) => ids.has(l.id_number)),
      })
    } finally { setDownloading(false) }
  }

  const empty = !loading && !error && (data?.usage_rows ?? 0) === 0
  const tile = (label: string, value: string): JSX.Element => (
    <div style={{ minWidth: 120, padding: '8px 14px', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
      <div style={{ fontSize: '0.7rem', color: '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0b3d2e', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  )
  const select: CSSProperties = { padding: '6px 10px', fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 8, background: '#fff' }
  const btn: CSSProperties = { background: '#0b3d2e', color: '#fff', border: '1px solid #0b3d2e', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.8rem', color: '#6b6b6b', display: 'flex', alignItems: 'center', gap: 6 }}>
            Season
            <select value={season || data?.season || ''} onChange={(e) => { setOpen(null); setSeason(e.target.value) }} style={select} aria-label="Season">
              {(data?.seasons ?? (season ? [season] : [])).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.8rem', color: '#0b3d2e', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={matchedOnly} onChange={(e) => { setOpen(null); setMatchedOnly(e.target.checked) }} />
            Matched users only
            {data && matchedOnly && summary.unmatched > 0 && <span style={{ color: '#6b6b6b' }}>({summary.unmatched} ID-card holder{summary.unmatched === 1 ? '' : 's'} with no Info Hub user hidden)</span>}
          </label>
          <button type="button" style={{ ...btn, opacity: !data || downloading ? 0.6 : 1 }} disabled={!data || downloading} onClick={onDownload}>
            {downloading ? 'Building workbook…' : 'Download'}
          </button>
          <span style={{ fontSize: '0.75rem', color: '#8a8577' }}>
            {loading ? 'Loading…' : data?.generated_at ? `Refreshed ${fmtDateTime(data.generated_at)} (nightly at 02:50).` : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} aria-label="Season summary">
          {tile(`Comps ${data?.season ?? ''}`, loading ? '…' : int.format(summary.comps))}
          {tile('Total value', loading ? '…' : gbp.format(summary.value))}
          {tile('People', loading ? '…' : int.format(summary.people))}
        </div>
      </div>

      {error && <div className="placeholder" style={{ borderColor: '#a33' }}><strong>Couldn&apos;t load comps</strong><p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p></div>}
      {empty ? (
        <div className="placeholder"><strong>Awaiting first refresh</strong><p>The comps cache is empty — run the Sync Hub job <code>Refresh-Comps</code> to populate it.</p></div>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '68vh', overflowY: 'auto', border: '1px solid #e5e1d6', borderRadius: 8, background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1000 }}>
            <thead>
              <tr>
                <th style={TH} onClick={() => onSort('name')}>Name{arrow('name')}</th>
                <th style={TH} onClick={() => onSort('status')}>Status{arrow('status')}</th>
                <th style={{ ...TH, textAlign: 'right' }} onClick={() => onSort('id_number')}>ID No{arrow('id_number')}</th>
                <th style={TH} onClick={() => onSort('card_no')}>Card No{arrow('card_no')}</th>
                <th style={{ ...TH, textAlign: 'right' }} onClick={() => onSort('comps')}>Comps {data?.season ?? ''}{arrow('comps')}</th>
                <th style={{ ...TH, textAlign: 'right' }} onClick={() => onSort('prev_comps')}>Comps {data?.prev_season ?? 'last season'}{arrow('prev_comps')}</th>
                <th style={TH} onClick={() => onSort('last_used')}>Last comp used{arrow('last_used')}</th>
                <th style={{ ...TH, textAlign: 'right' }} onClick={() => onSort('value')}>Value {data?.season ?? ''}{arrow('value')}</th>
                {!matchedOnly && <th style={TH} onClick={() => onSort('matched')}>Matched{arrow('matched')}</th>}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => {
                const isOpen = open === p.id_number
                const lines = linesByPerson.get(p.id_number) ?? []
                const cols = matchedOnly ? 8 : 9
                return (
                  <PersonRows key={p.id_number} p={p} lines={lines} isOpen={isOpen} cols={cols} showMatched={!matchedOnly} onToggle={() => setOpen(isOpen ? null : p.id_number)} />
                )
              })}
              {people.length === 0 && !loading && (
                <tr><td style={TD} colSpan={matchedOnly ? 8 : 9}>No comps in {data?.season ?? 'this season'}{matchedOnly ? ' for matched users — untick “Matched users only” to see every ID-card holder' : ''}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {data?.value_basis && (
        <p style={{ fontSize: '0.72rem', color: '#8a8577', margin: '8px 0 0' }}>
          Value: {data.value_basis}. Season = NYMR year (1 March – end February) of the date the comp was issued, as the Info Hub Comps app counts them. A refunded comp counts −1.
        </p>
      )}
    </>
  )
}

function PersonRows({ p, lines, isOpen, cols, showMatched, onToggle }: { p: CompPerson; lines: CompLine[]; isOpen: boolean; cols: number; showMatched: boolean; onToggle: () => void }) {
  const total = lines.reduce((a, l) => a + l.qty, 0)
  const totalValue = lines.reduce((a, l) => a + l.value, 0)
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer', background: isOpen ? '#eaf2ee' : undefined }} aria-expanded={isOpen}>
        <td style={{ ...TD, whiteSpace: 'nowrap', fontWeight: 600, color: '#0b3d2e' }}>{isOpen ? '▾ ' : '▸ '}{personName(p)}{p.card_inactive ? <span title="ID card inactive in OnTrack" style={{ marginLeft: 6, fontSize: '0.7rem', color: '#a33' }}> (card inactive)</span> : null}</td>
        <td style={TD}>{p.status}</td>
        <td style={{ ...TD, ...NUM }}>{p.id_number}</td>
        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtCard(p.card_no)}</td>
        <td style={{ ...TD, ...NUM }}>{int.format(p.comps)}</td>
        <td style={{ ...TD, ...NUM }}>{int.format(p.prev_comps)}</td>
        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDate(p.last_used)}</td>
        <td style={{ ...TD, ...NUM }}>{gbp.format(p.value)}</td>
        {showMatched && <td style={TD}>{p.matched ? 'Yes' : <span style={{ color: '#a33' }}>No</span>}</td>}
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={cols} style={{ padding: '4px 8px 12px 28px', background: '#f7f9f7', borderBottom: '1px solid #e5e1d6' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ ...TH, position: 'static', cursor: 'default', background: 'transparent' }}>Session</th>
                  <th style={{ ...TH, position: 'static', cursor: 'default', background: 'transparent' }}>Session date &amp; time</th>
                  <th style={{ ...TH, position: 'static', cursor: 'default', background: 'transparent' }}>Issued</th>
                  <th style={{ ...TH, position: 'static', cursor: 'default', background: 'transparent', textAlign: 'right' }}>Qty</th>
                  <th style={{ ...TH, position: 'static', cursor: 'default', background: 'transparent', textAlign: 'right' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={`${l.txn_no}-${l.txn_seq}`}>
                    <td style={TD}>{l.session_desc ?? '(no session)'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDateTime(l.session_at)}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDateTime(l.sale_at)}</td>
                    <td style={{ ...TD, ...NUM, color: l.qty < 0 ? '#c0392b' : undefined }}>{int.format(l.qty)}</td>
                    <td style={{ ...TD, ...NUM, color: l.value < 0 ? '#c0392b' : undefined }} title={l.value_basis}>{gbp.format(l.value)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...TD, fontWeight: 700, borderBottom: 'none' }} colSpan={3}>Comps total</td>
                  <td style={{ ...TD, ...NUM, fontWeight: 700, borderBottom: 'none' }}>{int.format(total)}</td>
                  <td style={{ ...TD, ...NUM, fontWeight: 700, borderBottom: 'none' }}>{gbp.format(totalValue)}</td>
                </tr>
              </tbody>
            </table>
            {p.email && <div style={{ fontSize: '0.72rem', color: '#8a8577', marginTop: 6 }}>Info Hub user: {p.email}</div>}
          </td>
        </tr>
      )}
    </>
  )
}
